/**
 * Drobné UI kusy modulu Směny — karta se statistikou, modál, pole formuláře
 * a dlaždice směny v kalendáři. Sdílí je plán, šablony i náklady.
 */
import { Edit2, Trash2 } from 'lucide-react';
import ModalShell from '../../../components/ui/ModalShell';
import { SECTION_COLOR, barvaSmen } from '../spolecne';

// ── StatCard ───────────────────────────────────────────────────
export function StatCard({ icon: Icon, label, value, color, isDark }) {
    return (
        <div style={{
            ...(isDark ? {
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(32px)',
                WebkitBackdropFilter: 'blur(32px)',
            } : {
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-default)',
                boxShadow: 'var(--shadow-xs)',
            }),
            borderRadius: 10, padding: '14px 16px',
            display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 130px', minWidth: 0,
        }}>
            <div style={{ width: 38, height: 38, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={17} style={{ color }} />
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.1 }}>{value}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap' }}>{label}</div>
            </div>
        </div>
    );
}

// ── Modal ──────────────────────────────────────────────────────
// Tenký obal nad normou (`ModalShell`): portál do body, nad mobilní lištou,
// na mobilu sheet zdola, na desktopu na střed. Dřív si Směny kreslily modal
// samy — bez portálu, takže na telefonu mohla spodní tlačítka překrýt lišta.
// `isMobile` zůstává v podpisu kvůli volajícím; mobil si ModalShell pozná sám.
export function Modal({ title, onClose, children, isDark, wide = false, accentColor }) {
    return (
        <ModalShell nadpis={title} onClose={onClose} isDark={isDark}
            sirka={wide ? 680 : 520} barva={accentColor || SECTION_COLOR}>
            <div style={{ padding: '16px 16px calc(16px + env(safe-area-inset-bottom, 0px))' }}>
                {children}
            </div>
        </ModalShell>
    );
}

// ── Input helpers ──────────────────────────────────────────────
export function inputSt(isDark) {
    return {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        color: isDark ? '#e2e8f0' : 'var(--text-primary)',
        borderRadius: 7, padding: '10px 14px',
        fontSize: 13, width: '100%', outline: 'none',
        colorScheme: isDark ? 'dark' : 'light',
        boxSizing: 'border-box',
        WebkitAppearance: 'none',
        transition: 'border-color 0.15s ease',
    };
}

export function Label({ children }) {
    return <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>{children}</div>;
}

export function FormGrid({ children, cols = 2 }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 14 }}>
            {children}
        </div>
    );
}

/**
 * Potvrzení a zrušení pod formulářem.
 *
 * `form` = id formuláře, ke kterému submit patří. V inspektoru stojí tahle
 * lišta v patičce `SidePanel`, tedy mimo `<form>` — bez atributu by tlačítko
 * nic neodeslalo. Uvnitř formuláře se `form` vynechá a `marginTop` odsadí
 * lištu od posledního pole; v patičce ne, tam odsazení dělá panel sám.
 */
export function ModalActions({ onCancel, submitLabel = 'Uložit', loading, form = null, onSmazat = null }) {
    return (
        <div style={{ display: 'flex', gap: 10, marginTop: form ? 0 : 24, paddingTop: form ? 0 : 16, borderTop: form ? 'none' : '1px solid var(--border-default)' }}>
            <button type="submit" {...(form ? { form } : null)} disabled={loading} style={{
                background: SECTION_COLOR, color: '#000', border: 'none',
                borderRadius: 7, padding: '11px 24px', fontWeight: 600, fontSize: 13,
                cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
                transition: 'opacity 0.15s',
            }}>{loading ? 'Ukládám…' : submitLabel}</button>
            <button type="button" onClick={onCancel} style={{
                background: 'transparent', color: 'var(--text-muted)',
                border: '1px solid var(--border-default)', borderRadius: 7,
                padding: '11px 20px', fontSize: 13, cursor: 'pointer',
                transition: 'all 0.15s',
            }}>Zrušit</button>
            {onSmazat && (
                <button type="button" onClick={onSmazat} style={{
                    marginLeft: 'auto', background: 'transparent', color: '#EF4444',
                    border: '1px solid rgba(239,68,68,0.35)', borderRadius: 7,
                    padding: '11px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}>Smazat</button>
            )}
        </div>
    );
}

// ── Shift pill (in calendar) ───────────────────────────────────
export function SmenaChip({ s, isOtevrena, size = 'normal', onEdit, onDelete, isDark }) {
    const barva = isOtevrena ? barvaSmen(isDark) : (s.sablon?.barva || '#6B7280');
    const ikona = s.sablon?.kariera?.nazev?.[0] || '';
    const isCompact = size === 'compact';

    return (
        <div style={{
            borderRadius: 6, padding: isCompact ? '2px 5px' : '6px 8px',
            marginBottom: isCompact ? 2 : 4,
            background: isOtevrena ? 'rgba(147,51,234,0.12)' : `${barva}18`,
            border: `1px solid ${isOtevrena ? 'rgba(147,51,234,0.3)' : `${barva}40`}`,
            borderStyle: isOtevrena ? 'dashed' : 'solid',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                <span style={{ fontSize: isCompact ? 10 : 11, fontWeight: 500, color: isOtevrena ? '#C084FC' : barva, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {isOtevrena
                        ? (isCompact ? '🔓' : `🔓 ${s.obsazeno ?? 0}/${s.kapacita ?? 1}`)
                        : isCompact
                            ? `${ikona} ${s.zamestnanec?.jmeno?.[0] ?? '?'}.${s.zamestnanec?.prijmeni?.[0] ?? '?'}`
                            : `${ikona} ${s.zamestnanec?.jmeno} ${s.zamestnanec?.prijmeni}`
                    }
                </span>
                {!isCompact && onEdit && (
                    <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        <button onClick={e => { e.stopPropagation(); onEdit(s); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted)', display: 'flex' }}>
                            <Edit2 size={11} />
                        </button>
                        <button onClick={e => { e.stopPropagation(); onDelete(s.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted)', display: 'flex' }}>
                            <Trash2 size={11} />
                        </button>
                    </div>
                )}
            </div>
            <div style={{ fontSize: isCompact ? 9 : 10, color: isOtevrena ? '#C084FC' : barva, opacity: 0.75, marginTop: 1 }}>
                {s.cas_od?.slice(0, 5)}–{s.cas_do?.slice(0, 5)}
                {!isCompact && s.sablon?.nazev && ` · ${s.scheduling_group.nazev}`}
            </div>
        </div>
    );
}
